import { useState, useEffect, useRef } from 'react';
import { getLocationsByRegion } from '../../lib/sanity';
import { useLocation } from '../../context/LocationContext';
import { Link } from 'react-router-dom';

/**
 * RegionDropdown component for selecting a location from a dropdown
 * Now displays the globally selected location and allows browsing all locations
 * 
 * @param {Object} props Component props
 * @param {string} props.selectedRegion Currently selected region (legacy)
 * @param {Function} props.onRegionChange Function to call when region changes (legacy)
 * @param {string} props.className Additional CSS classes
 */
export default function RegionDropdown({ selectedRegion, onRegionChange, className = '' }) {
  // Get global selected location
  const { selectedLocation, selectLocation } = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [regions, setRegions] = useState({});
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Fetch regions and their location counts
  useEffect(() => {
    async function fetchRegions() {
      try {
        setLoading(true);
        const locationsByRegion = await getLocationsByRegion();
        setRegions(locationsByRegion);
      } catch (error) {
        console.error('Error fetching regions:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchRegions();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format region name for display
  const formatRegionName = (region) => {
    if (!region) return 'Select Location';
    if (region === 'washington') return 'Washington State';
    if (region === 'new-york') return 'New York';
    
    return region
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get the current display name - prioritize selected location
  const displayText = selectedLocation 
    ? selectedLocation.name 
    : 'Select Location';

  // Handle location selection
  const handleLocationSelect = (location) => {
    selectLocation(location);
    setIsOpen(false);
  };

  // Get sorted region names
  const regionNames = Object.keys(regions).sort((a, b) => {
    const order = { 'washington': 1, 'indiana': 2, 'new-york': 3 };
    const orderA = order[a] || 99;
    const orderB = order[b] || 99;
    
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });

  // Custom sort order for Washington locations
  const washingtonLocationOrder = [
    'bothell',
    'redmond',
    'maple-valley',
    'gig-harbor',
    'tacoma',
    'puyallup',
    'richland',
    'kennewick'
  ];

  // Sort locations within each region
  const getSortedLocations = (region, locations) => {
    if (!locations) return [];
    
    if (region === 'washington') {
      // Sort Washington locations using the custom order
      return [...locations].sort((a, b) => {
        // Extract city from slug (e.g., "bothell-wa" -> "bothell")
        const cityA = a.slug?.split('-').slice(0, -1).join('-') || a.slug;
        const cityB = b.slug?.split('-').slice(0, -1).join('-') || b.slug;
        
        const indexA = washingtonLocationOrder.indexOf(cityA);
        const indexB = washingtonLocationOrder.indexOf(cityB);
        
        // If both are in the order list, sort by order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // If only one is in the list, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        // Otherwise, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
    }
    
    // For other regions, sort alphabetically by name
    return [...locations].sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button 
        className="flex items-center justify-between text-brand-navy border border-brand-navy rounded-full px-3 2xl:px-4 py-2 2xl:py-2.5 hover:bg-brand-navy hover:text-white transition-colors w-[180px] 2xl:w-[240px] text-sm 2xl:text-base"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 2xl:h-5 2xl:w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="flex-1 truncate text-center px-1 2xl:px-2">{displayText}</span>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 2xl:h-5 2xl:w-5 flex-shrink-0 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-gray-500 text-center">Loading locations...</div>
          ) : regionNames.length === 0 ? (
            <div className="px-4 py-3 text-gray-500 text-center">No locations available</div>
          ) : (
            <div className="py-2">
              {regionNames.map((region) => (
                <div key={region} className="mb-2">
                  {/* Region Header */}
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <h4 className="text-sm font-bold text-brand-navy">{formatRegionName(region)}</h4>
                  </div>
                  
                  {/* Locations in this region */}
                  <ul className="py-1">
                    {getSortedLocations(region, regions[region])?.map((location) => (
                      <li 
                        key={location._id}
                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${selectedLocation?._id === location._id ? 'bg-blue-50 font-medium' : ''}`}
                        onClick={() => handleLocationSelect(location)}
                        role="option"
                        aria-selected={selectedLocation?._id === location._id}
                      >
                        <span className="text-sm">{location.name}</span>
                        {selectedLocation?._id === location._id && (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              
              {/* View All Locations Link */}
              <div className="border-t border-gray-200 mt-2">
                <Link
                  to="/locations"
                  className="block px-4 py-3 text-sm text-center text-brand-navy hover:bg-gray-100 font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  View All Locations →
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
